import {
  Bell,
  Boxes,
  Briefcase,
  Building2,
  ChartPie,
  ChevronDown,
  ChevronRight,
  ChevronsUpDown,
  Handshake,
  PanelLeft,
  Plus,
  Search,
  Settings,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import { type ReactNode } from 'react';

import mascoteUrl from '@/assets/mascote-96.png';
import logoUrl from '@/assets/sancus-logo.svg';

/*
 * A moldura do CRM redesenhada para a vitrine: o trilho azul com a Lexi, o
 * painel de módulos e a barra superior. Copia as classes e os tokens de
 * `components/layout` (sidebar-rail, sidebar-panel, app-topbar) para a tela
 * da landing ser a tela do produto, e não a de um sistema qualquer.
 *
 * Não reaproveita os componentes de verdade porque eles dependem do router,
 * da sessão e da filial ativa — e a landing não carrega o CRM.
 */

export type RailArea = 'Trabalho' | 'Relacionamento' | 'Gestão';

const railAreas: Array<[RailArea, LucideIcon]> = [
  ['Trabalho', Briefcase],
  ['Relacionamento', Handshake],
  ['Gestão', ChartPie],
];

export interface PanelItem {
  label: string;
  icon: LucideIcon;
  active?: boolean;
  /** Item com subseções: mostra a seta, como no menu de verdade. */
  nested?: boolean;
  badge?: number;
}

function PanelRow({ label, icon: Icon, active, nested, badge }: PanelItem) {
  return (
    <li
      className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-[13.5px] ${
        active
          ? 'bg-primary-container text-primary font-semibold'
          : 'text-on-surface-variant font-medium'
      }`}
    >
      <Icon className="size-[18px] shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
      {badge !== undefined && (
        <span className="bg-primary text-on-primary ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none">
          {badge}
        </span>
      )}
      {nested && <ChevronRight className="ml-auto size-4 shrink-0" aria-hidden="true" />}
    </li>
  );
}

interface AppFrameProps {
  area: RailArea;
  items: PanelItem[];
  /** O que aparece depois do nome do escritório na barra superior. */
  section: string;
  children: ReactNode;
}

export function AppFrame({ area, items, section, children }: AppFrameProps) {
  return (
    <div className="bg-surface text-on-surface flex h-full font-sans">
      {/* Trilho */}
      <div className="bg-nav-rail flex w-16 shrink-0 flex-col items-center">
        <div className="py-8">
          <img
            src={mascoteUrl}
            alt=""
            width="48"
            height="48"
            className="size-12 rounded-full object-contain"
          />
        </div>
        <div className="flex flex-1 flex-col items-center px-2">
          {railAreas.map(([label, Icon]) => (
            <div key={label} className="flex h-12 w-10 items-center justify-center">
              <span
                className={`flex size-10 items-center justify-center rounded-lg ${
                  label === area ? 'bg-nav-rail-active text-on-nav-rail-active' : 'text-on-nav-rail'
                }`}
              >
                <Icon className="size-[18px]" aria-hidden="true" />
              </span>
            </div>
          ))}
        </div>
        <div className="border-on-nav-rail/15 text-on-nav-rail flex h-14 w-full items-center justify-center border-t">
          <PanelLeft className="size-[18px]" aria-hidden="true" />
        </div>
      </div>

      {/* Painel de módulos */}
      <div className="bg-surface-container-lowest border-outline-variant flex w-60 shrink-0 flex-col border-r">
        <div className="flex flex-col gap-5 px-5 pb-5 pt-6">
          <img
            src={logoUrl}
            alt=""
            width="179"
            height="64"
            className="h-16 w-auto self-center object-contain"
          />
          <span className="text-on-surface-variant text-[12px] font-semibold uppercase leading-none tracking-[0.12em]">
            {area}
          </span>
        </div>
        <div className="mb-3 px-3">
          <div className="border-outline-variant bg-surface-container-low flex items-center gap-2.5 rounded-lg border px-3 py-2">
            <span className="bg-primary-container text-primary flex size-7 shrink-0 items-center justify-center rounded-md">
              <Building2 className="size-3.5" aria-hidden="true" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[13px] font-semibold leading-tight">Matriz</span>
              <span className="text-on-surface-variant mt-0.5 block truncate text-[10px] leading-tight">
                SP-01 · padrão
              </span>
            </span>
            <ChevronsUpDown
              className="text-on-surface-variant size-3.5 shrink-0"
              aria-hidden="true"
            />
          </div>
        </div>
        <div className="mb-3 px-3">
          <span className="bg-primary text-on-primary flex h-9 w-full items-center justify-center gap-2 rounded-lg text-[13px] font-semibold shadow-sm">
            <Plus className="size-4" aria-hidden="true" />
            Criar novo
          </span>
        </div>
        <ul className="flex-1 space-y-0.5 px-2.5">
          {items.map((item) => (
            <PanelRow key={item.label} {...item} />
          ))}
        </ul>
        <ul className="border-outline-variant mx-2.5 space-y-0.5 border-t py-2">
          <PanelRow label="Módulos" icon={Boxes} />
          <PanelRow label="Configurações" icon={Settings} nested />
          <PanelRow label="Segurança" icon={ShieldCheck} nested />
        </ul>
        <div className="border-outline-variant flex items-center justify-between border-t px-4 py-3">
          <span className="text-on-surface-variant text-[12px] font-medium">Fixar menu</span>
          <span className="bg-primary relative h-5 w-9 rounded-full">
            <span className="absolute left-[1.125rem] top-0.5 size-4 rounded-full bg-white shadow" />
          </span>
        </div>
      </div>

      {/* Barra superior e conteúdo */}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="border-outline-variant bg-surface-container-lowest h-15 flex shrink-0 items-center gap-3 border-b px-5">
          <div className="flex min-w-0 items-center gap-2">
            <Building2 className="text-on-surface-variant size-4" aria-hidden="true" />
            <span className="whitespace-nowrap text-[13px] font-medium">Andrade &amp; Costa</span>
            <span className="text-outline">/</span>
            <span className="text-on-surface-variant whitespace-nowrap text-[13px]">{section}</span>
          </div>
          <div className="border-outline-variant bg-surface-container-low mx-auto flex h-9 w-full max-w-md items-center gap-2 rounded-lg border px-3">
            <Search className="text-on-surface-variant size-4 shrink-0" aria-hidden="true" />
            <span className="text-on-surface-variant flex-1 truncate text-[13px]">
              Buscar ou perguntar ao Sancus...
            </span>
            <kbd className="border-outline-variant bg-surface-container-lowest text-on-surface-variant rounded border px-1.5 py-0.5 font-sans text-[10px]">
              Ctrl K
            </kbd>
          </div>
          <span className="text-on-surface-variant relative flex size-9 items-center justify-center">
            <Bell className="size-4.5" aria-hidden="true" />
            <span className="bg-error border-surface-container-lowest absolute right-2 top-2 size-1.5 rounded-full border" />
          </span>
          <span className="bg-primary text-on-primary flex h-9 items-center gap-2 rounded-lg px-3 text-[13px] font-semibold">
            <Plus className="size-4" aria-hidden="true" />
            Criar
          </span>
          <span className="flex items-center gap-2.5 py-1 pl-1 pr-2.5">
            <span className="bg-secondary-fixed text-on-secondary-fixed flex size-7 items-center justify-center rounded-full text-[10px] font-semibold">
              AC
            </span>
            <span className="whitespace-nowrap text-[14px] font-medium">Ana Costa</span>
            <ChevronDown className="text-outline size-4" aria-hidden="true" />
          </span>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden px-8 py-7">{children}</div>
      </div>
    </div>
  );
}

/** Cabeçalho de página do CRM (components/page/page-header). */
export function FramePageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow: string;
  title: string;
  description: string;
  actions: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-8">
      <div>
        <p className="text-on-surface-variant text-micro mb-1.5 uppercase">{eyebrow}</p>
        <h3 className="text-headline-lg">{title}</h3>
        <p className="text-on-surface-variant text-body-sm mt-1.5">{description}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2 pt-0.5">{actions}</div>
    </div>
  );
}

/** Botões no formato do design system (packages/ui/button). */
export function FrameButton({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: 'default' | 'outline';
}) {
  return (
    <span
      className={`inline-flex h-9 items-center gap-2 rounded-lg px-3.5 text-[13px] font-semibold [&_svg]:size-4 ${
        variant === 'outline'
          ? 'border-outline-variant bg-surface-container-lowest text-on-surface border'
          : 'bg-primary text-on-primary'
      }`}
    >
      {children}
    </span>
  );
}

const badgeTone = {
  neutral: 'bg-surface-container-high text-on-surface-variant ring-outline-variant',
  info: 'bg-secondary-fixed text-on-secondary-fixed ring-secondary/20',
  success: 'bg-tertiary-fixed text-on-tertiary-fixed-variant ring-tertiary/25',
  warning: 'bg-warning-container text-on-warning-container ring-warning/30',
  error: 'bg-error-container text-on-error-container ring-error/30',
} as const;

export type BadgeTone = keyof typeof badgeTone;

/** Selo no formato do design system (packages/ui/badge). */
export function FrameBadge({ tone, children }: { tone: BadgeTone; children: ReactNode }) {
  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${badgeTone[tone]}`}
    >
      {children}
    </span>
  );
}
